import React from 'react';
import { useCalendar } from '../hooks/useCalendar';
import { trackCalendarEventClick } from '../services/enpalForYouService';
import { CalendarEvent } from '../types';
import { Button } from './Button';

interface AddToCalendarButtonProps {
  event: CalendarEvent;
  filename?: string;
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}

/**
 * Button component that adds a calendar event to the device calendar
 *
 * @example
 * ```tsx
 * <AddToCalendarButton
 *   event={{
 *     title: 'Solar Panel Installation',
 *     description: 'Installation of solar panels at your property',
 *     location: '123 Main St, City',
 *     startDate: new Date('2024-02-15T10:00:00'),
 *     endDate: new Date('2024-02-15T14:00:00'),
 *   }}
 * >
 *   Add to Calendar
 * </AddToCalendarButton>
 * ```
 */
export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  event,
  filename,
  children,
  variant = 'primary',
  className,
}) => {
  const { addEvent, loading, error, isWebShareSupported } = useCalendar();

  const handleClick = async (): Promise<void> => {
    if (event.id) {
      trackCalendarEventClick(event.id);
    }

    try {
      await addEvent(event, { filename });
    } catch (err) {
      // Error is already handled by the hook
      console.error('Failed to add event to calendar:', err);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <Button
        variant={variant}
        onClick={handleClick}
        disabled={loading}
        className={className}
      >
        {loading ? 'Adding...' : children || 'Add to Calendar'}
      </Button>
      {error && (
        <div className="mt-1 text-center text-xs text-rose-600">{error}</div>
      )}
      {!isWebShareSupported && (
        <div className="mt-1 text-center text-xs text-enpal-stone-grey">
          A calendar file will be downloaded
        </div>
      )}
    </div>
  );
};

